'use client'

import {
	FileUpload,
	TFileUploadHandle,
	type TFileUploadProps,
} from '@qr-front/ui/components/FileUpload'
import { captureBuilderEvent } from 'features/generator/builderAnalytics'
import { isSubmittingAtom } from 'features/generator/GeneratorLayout/GeneratorFooter/NextButton'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { forwardRef, useEffect, useRef, useState } from 'react'

import {
	normalizeFileName,
	sanitizeFileName,
	transformUploadedFileNameForDisplay,
	type TUploadedMedia,
	uploadFile,
} from '@/services'
import { resizeImage, stripSvgForeignObjects } from '@/utils'

// MYQRCODE-10778 AC#13: opt-in, like RhcField's fieldName — callers not
// passing uploadPurpose don't fire builder_upload_file at all.
type TBuilderUploadPurpose = 'content_image' | 'qr_logo' | 'pdf'

const captureUploadOutcome = (
	uploadPurpose: TBuilderUploadPurpose | undefined,
	file: File,
	isSuccess: boolean,
) => {
	if (!uploadPurpose) return
	captureBuilderEvent('builder_upload_file', {
		upload_purpose: uploadPurpose,
		file_format: file.type.split('/')[1],
		file_size_kb: Math.round(file.size / 1024),
		is_success: isSuccess,
		...(isSuccess ? {} : { error_reason: 'upload_failed' }),
	})
}

export const isFileUploadingAtom = atom(false)

let activeFileUploadCount = 0

const updateIsFileUploading = (
	setIsFileUploading: (isUploading: boolean) => void,
	delta: 1 | -1,
) => {
	activeFileUploadCount = Math.max(0, activeFileUploadCount + delta)
	setIsFileUploading(activeFileUploadCount > 0)
}

const createFileKey = (fileName: string, suffix?: string) =>
	`${normalizeFileName(fileName)}-${suffix || globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`

export type TFileUploadToMediaStorageProps = Omit<
	TFileUploadProps,
	'onFileUpload' | 'onFileReplace' | 'onFileDelete' | 'files' | 'progress'
> & {
	initialFileList?: File[]
	isLogoUpload?: boolean
	onUploadStart?: (file: File, index: number) => void
	onUploadComplete: (
		uploaded: TUploadedMedia,
		original: File,
		index: number,
	) => void
	onUploadError?: (file: File, index: number) => void
	onDelete?: (file: File, index: number) => void
	resizeResolution?: { width: number; height: number }
	showPreviewButton?: boolean
	onClickShowPreview?: () => void
	maintainAspectRatio?: boolean
	maxFiles?: number
	uploadPurpose?: TBuilderUploadPurpose
}

export const FileUploadToMediaStorage = forwardRef<
	TFileUploadHandle,
	TFileUploadToMediaStorageProps
>(
	(
		{
			initialFileList = [],
			onUploadStart,
			onUploadComplete,
			onUploadError,
			onDelete,
			resizeResolution,
			maintainAspectRatio,
			maxFiles = 1,
			uploadPurpose,
			...restProps
		},
		ref,
	) => {
		const [files, setFiles] = useState<File[]>(
			initialFileList.slice(0, maxFiles),
		)
		const [progress, setProgress] = useState<Record<string, number>>({})
		const [fileKeys, setFileKeys] = useState<string[]>([])
		const isSubmitting = useAtomValue(isSubmittingAtom)
		const setIsFileUploading = useSetAtom(isFileUploadingAtom)

		const abortRefs = useRef<Record<string, AbortController>>({})
		const hasInitialized = useRef(false)
		const hasUploadStartedRef = useRef(false)
		const fileKeysRef = useRef<string[]>([])

		useEffect(() => {
			if (hasUploadStartedRef.current) return
			if (initialFileList.length > 0) {
				const initialFiles = initialFileList.slice(0, maxFiles)
				const initialKeys = initialFiles.map((file, index) =>
					createFileKey(file.name, `initial-${index}`),
				)
				setFiles(initialFiles)
				setFileKeys(initialKeys)
				fileKeysRef.current = initialKeys
				setProgress(
					Object.fromEntries(initialKeys.map((key) => [key, 100])),
				)
				hasInitialized.current = true
			} else if (!hasInitialized.current) {
				hasInitialized.current = true
			}
		}, [initialFileList, maxFiles])

		const filesRef = useRef<File[]>(files)

		useEffect(() => {
			filesRef.current = files
		}, [files])

		useEffect(() => {
			fileKeysRef.current = fileKeys
		}, [fileKeys])

		const handleUpload = async (
			incomingFile: File,
			isReplacement = false,
			replaceIndex?: number,
		) => {
			hasUploadStartedRef.current = true

			const sanitizedFileName = sanitizeFileName(incomingFile.name)
			const uploadKey = createFileKey(sanitizedFileName)

			const fileToUpload =
				sanitizedFileName !== incomingFile.name
					? new File([incomingFile], sanitizedFileName, {
							type: incomingFile.type,
						})
					: incomingFile

			const nextFiles = [...filesRef.current]
			const nextFileKeys = [...fileKeysRef.current]
			const previousFileKey =
				isReplacement && replaceIndex !== undefined
					? nextFileKeys[replaceIndex]
					: undefined
			const targetIndex =
				isReplacement && replaceIndex !== undefined
					? replaceIndex
					: nextFiles.length

			onUploadStart?.(fileToUpload, targetIndex)

			if (isReplacement && replaceIndex !== undefined) {
				nextFiles[replaceIndex] = fileToUpload
				nextFileKeys[replaceIndex] = uploadKey
			} else if (!isReplacement) {
				nextFiles.push(fileToUpload)
				nextFileKeys.push(uploadKey)
			}

			filesRef.current = nextFiles.slice(0, maxFiles)
			fileKeysRef.current = nextFileKeys.slice(0, maxFiles)
			setFiles(filesRef.current)
			setFileKeys(fileKeysRef.current)
			setProgress((prev) => ({
				...(previousFileKey
					? Object.fromEntries(
							Object.entries(prev).filter(
								([key]) => key !== previousFileKey,
							),
						)
					: prev),
				[uploadKey]: 0,
			}))
			updateIsFileUploading(setIsFileUploading, 1)
			const controller = new AbortController()
			if (previousFileKey) {
				abortRefs.current[previousFileKey]?.abort()
				delete abortRefs.current[previousFileKey]
			}
			abortRefs.current[uploadKey] = controller

			try {
				const sanitized = fileToUpload.type.includes('svg')
					? await stripSvgForeignObjects(fileToUpload)
					: fileToUpload

				const resized =
					sanitized.type.includes('svg') || !resizeResolution
						? sanitized
						: await resizeImage(
								sanitized,
								resizeResolution.width,
								resizeResolution.height,
							)

				const uploaded = await uploadFile(
					resized,
					(progress) =>
						setProgress((prev) => ({
							...prev,
							[uploadKey]: progress,
						})),
					controller.signal,
				)

				if (uploaded) {
					captureUploadOutcome(uploadPurpose, fileToUpload, true)
					onUploadComplete(uploaded, fileToUpload, targetIndex)
				} else if (!controller.signal.aborted) {
					const filteredFiles = filesRef.current.filter(
						(_, index) => index !== targetIndex,
					)
					const filteredKeys = fileKeysRef.current.filter(
						(_, index) => index !== targetIndex,
					)
					filesRef.current = filteredFiles
					fileKeysRef.current = filteredKeys
					setFiles(filteredFiles)
					setFileKeys(filteredKeys)
					setProgress((prev) => {
						const { [uploadKey]: _, ...rest } = prev
						return rest
					})
					captureUploadOutcome(uploadPurpose, fileToUpload, false)
					onUploadError?.(fileToUpload, targetIndex)
				}
			} catch (err) {
				if (controller.signal.aborted) return
				console.error('Upload failed', err)
				const filteredFiles = filesRef.current.filter(
					(_, index) => index !== targetIndex,
				)
				const filteredKeys = fileKeysRef.current.filter(
					(_, index) => index !== targetIndex,
				)
				filesRef.current = filteredFiles
				fileKeysRef.current = filteredKeys
				setFiles(filteredFiles)
				setFileKeys(filteredKeys)
				setProgress((prev) => {
					const { [uploadKey]: _, ...rest } = prev
					return rest
				})
				captureUploadOutcome(uploadPurpose, fileToUpload, false)
				onUploadError?.(fileToUpload, targetIndex)
			} finally {
				delete abortRefs.current[uploadKey]
				updateIsFileUploading(setIsFileUploading, -1)
			}
		}

		const handleReplace = (file: File, index: number) => {
			handleUpload(file, true, index)
		}

		const handleDelete = (file: File, index: number) => {
			const fileKey = fileKeysRef.current[index]
			if (fileKey) {
				abortRefs.current[fileKey]?.abort()
				delete abortRefs.current[fileKey]
			}

			const nextFiles = filesRef.current.filter((_, i) => i !== index)
			const nextFileKeys = fileKeysRef.current.filter(
				(_, i) => i !== index,
			)
			filesRef.current = nextFiles
			fileKeysRef.current = nextFileKeys
			setFiles(nextFiles)
			setFileKeys(nextFileKeys)
			setProgress((prev) => {
				if (!fileKey) {
					return prev
				}
				const { [fileKey]: _, ...rest } = prev
				return rest
			})

			onDelete?.(file, index)
		}

		return (
			// @ts-expect-error TODO: find a solution to properly type image resolution props while also supporting passing rest props
			<FileUpload
				ref={ref}
				files={files}
				maxFiles={maxFiles}
				onFileUpload={handleUpload}
				onFileReplace={handleReplace}
				onFileDelete={handleDelete}
				progress={progress}
				disabled={isSubmitting}
				maintainAspectRatio={maintainAspectRatio}
				getFileKey={(file, index) =>
					fileKeys[index] || createFileKey(file.name, `${index}`)
				}
				{...restProps}
				fileNameTransformer={
					restProps.fileNameTransformer ||
					transformUploadedFileNameForDisplay
				}
			/>
		)
	},
)

FileUploadToMediaStorage.displayName = 'FileUploadToMediaStorage'
